export type OrganizationConnectionPresentationRow = {
  personId: string;
  roles: readonly string[];
  status: string;
  uid: string;
};

export type PersonProfilePresentationRow = {
  displayName: string | null;
  email: string | null;
  phoneNumber: string | null;
  photoUrl: string | null;
  status: string;
  uid: string;
};

export type PeopleManagerPresentationRow = {
  connection: OrganizationConnectionPresentationRow;
  personProfile: PersonProfilePresentationRow | null | undefined;
};

export function peopleRowPersonLabel(row: PeopleManagerPresentationRow): string {
  const profile = row.personProfile;
  if (profile === undefined) {
    return "Loading profile...";
  }
  if (profile === null) {
    return "Profile unavailable";
  }
  return profile.displayName ?? profile.email ?? profile.phoneNumber ?? "Profile unavailable";
}

export function peopleRowManualSponsorLabel(
  row: PeopleManagerPresentationRow,
  index: number,
): string {
  const profile = row.personProfile;
  return profile?.displayName ?? profile?.email ?? `Connected person ${index + 1}`;
}

export function peopleRowSearchText(
  row: PeopleManagerPresentationRow,
  options: {
    legacyRolesLabel?: string;
    roleLabels?: readonly string[];
  } = {},
): string {
  return [
    peopleRowPersonLabel(row),
    row.personProfile?.email,
    row.personProfile?.phoneNumber,
    row.personProfile?.status,
    row.connection.personId,
    row.connection.uid,
    row.connection.status,
    options.legacyRolesLabel,
    ...(options.roleLabels ?? []),
  ]
    .join(" ")
    .toLowerCase();
}

export function renderPeopleProfileMeta(row: PeopleManagerPresentationRow): string {
  const profile = row.personProfile;
  if (profile === undefined) {
    return `<p class="meta">Loading profile...</p>`;
  }
  if (profile === null) {
    return `<p class="meta">Profile unavailable</p>${renderTechnicalId(
      row.connection.personId,
      "Person ID",
    )}`;
  }
  const details = [
    profile.email,
    profile.phoneNumber,
    profile.status && profile.status !== "active" ? profile.status : null,
  ].filter((value): value is string => Boolean(value));

  return `${details.length ? `<p class="meta">${escapeHtml(details.join(" · "))}</p>` : ""}${renderTechnicalId(
    row.connection.personId,
    "Person ID",
  )}`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function shortTechnicalId(value: string): string {
  const normalized = value.trim();
  if (normalized.length <= 18) {
    return normalized;
  }

  return `${normalized.slice(0, 10)}...${normalized.slice(-5)}`;
}

function renderTechnicalId(value: string, label: string): string {
  const normalized = value.trim();
  if (!normalized) {
    return "";
  }

  return `<small class="technical-id"><span>${escapeHtml(label)}</span><code title="${escapeHtml(
    normalized,
  )}">${escapeHtml(shortTechnicalId(normalized))}</code></small>`;
}
